import React from 'react';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useShowContext } from 'framework/show';
import useVendorService from '../../useVendorService';
import { VendorDto } from '../../types';
import { AGREEMENT_TYPES, VENDOR_DURACTION_TYPE } from '../../constants';
import FinalizeAgreementForm from 'domain/finance/vendors/createupdate/Create/FinalizeAgreementForm';
import AgreementActionButton from './AgreementActionButton';
import { useAgreementPermissions, AGREEMENT_PERMISSIONS } from './AgreementPermissionsContext';

interface FinalizeAgreementButtonProps {
  vendorData?: VendorDto;
}

const FinalizeAgreementButton = ({ vendorData }: FinalizeAgreementButtonProps) => {
  const { finalizeAgreement } = useVendorService();
  const { data: agreement } = useShowContext();
  const { hasPermission } = useAgreementPermissions();

  const canFinalize =
    agreement?.status === 'DRAFT' &&
    vendorData?.profileStatus === 'FINALIZED' &&
    vendorData?.isCompliant === true &&
    hasPermission(AGREEMENT_PERMISSIONS.FINALIZE);

  const agreementType = agreement?.agreementType;

  const getValidationSchema = () =>
    Promise.resolve({
      durationType: { type: 'enum', required: true },
      startDate: { type: 'date', required: true },
      endDate: {
        type: 'date',
        required: {
          value: true,
          when: {
            $durationType: { in: [VENDOR_DURACTION_TYPE.ONE_TIME, VENDOR_DURACTION_TYPE.FIXED_TERM] },
          },
        },
      },
      publicOfferLink: {
        type: 'string',
        required: agreementType === AGREEMENT_TYPES.PUBLIC_OFFER,
      },
      signedContractAttachment: {
        type: 'array',
        required: agreementType === AGREEMENT_TYPES.CONTRACT,
        maxSize: 1,
      },
    });

  const handleSubmit = async (agreementId: string, values: any) => {
    return finalizeAgreement(agreementId, values);
  };

  const initialData = agreement
    ? {
        durationType: agreement.durationType || '',
        startDate: agreement.startDate || '',
        endDate: agreement.endDate || '',
        publicOfferLink: agreement.publicOfferLink || '',
        signedContractAttachment: agreement.signedContractAttachment ? [agreement.signedContractAttachment] : [],
      }
    : {};

  return (
    <AgreementActionButton
      vendorData={vendorData}
      icon={<HandshakeIcon />}
      color="success"
      formTitle="Finalize agreement"
      tooltip="Finalize agreement"
      canPerformAction={canFinalize}
      onSubmit={handleSubmit}
      getValidationSchema={getValidationSchema}
      initialData={initialData}
    >
      <FinalizeAgreementForm agreementType={agreementType} />
    </AgreementActionButton>
  );
};

export default FinalizeAgreementButton;
